import { Link } from "react-router-dom";

import type { ParentMember, TaskOccurrence } from "../lib/types";

interface TaskCollectionItemProps {
  task: TaskOccurrence;
  member?: ParentMember;
  offline: boolean;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canManage?: boolean;
  onArchive: () => void;
  onMove: (direction: -1 | 1) => void;
}

const statusLabel = {
  OPEN: "待完成",
  COMPLETED: "已完成",
  SKIPPED: "已略過",
  CANCELLED: "已取消",
} as const;

export function TaskCollectionItem({
  task,
  member,
  offline,
  busy,
  canMoveUp,
  canMoveDown,
  canManage = true,
  onArchive,
  onMove,
}: TaskCollectionItemProps) {
  const assignee = task.assignment.type === "HOUSEHOLD" ? "全家" : member?.displayName ?? "家人";
  const mutable = canManage && !offline && !busy && task.status === "OPEN";

  return (
    <li className={`collection-task status-${task.status.toLowerCase()}`}>
      <div className="collection-task-main">
        <div className="task-title-line">
          <h3>{task.title}</h3>
          <span className={`status-pill status-${task.status.toLowerCase()}`}>{statusLabel[task.status]}</span>
        </div>
        <p className="collection-meta">
          <span>{assignee}</span>
          <span>{formatSchedule(task)}</span>
          <span>{task.points} 點</span>
        </p>
      </div>
      <div className="task-actions" aria-label={`「${task.title}」操作`}>
        <button type="button" disabled={!mutable || !canMoveUp} onClick={() => onMove(-1)} aria-label={`將「${task.title}」往上移`}>
          ↑
        </button>
        <button type="button" disabled={!mutable || !canMoveDown} onClick={() => onMove(1)} aria-label={`將「${task.title}」往下移`}>
          ↓
        </button>
        {task.status === "OPEN" && canManage ? (
          <Link to={`/tasks/${encodeURIComponent(task.templateId)}/edit`} aria-label={`編輯「${task.title}」`}>
            編輯
          </Link>
        ) : (
          <span className="disabled-action" aria-hidden="true">編輯</span>
        )}
        {canManage ? (
          <button type="button" disabled={!mutable} onClick={onArchive} aria-label={`封存「${task.title}」`}>
            封存
          </button>
        ) : null}
      </div>
    </li>
  );
}

function formatSchedule(task: TaskOccurrence) {
  if (!task.scheduledDate) return "未排日期";
  const date = new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    month: "numeric",
    day: "numeric",
  }).format(new Date(`${task.scheduledDate}T12:00:00+08:00`));
  if (!task.hasDueTime || !task.dueAt) return date;
  const time = new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(task.dueAt));
  return `${date} ${time}`;
}
